import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, LogIn, ArrowLeft, Code } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/seo/SEOHead";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { signIn } = useAuth();
  const { toast } = useToast();

  // Track mouse position for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Error signing in",
        description: "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEOHead
        title="Sign in | LaunchGuide.io"
        description="Sign in to LaunchGuide to access playbooks, AI marketing tools, and your projects."
        pathname="/auth/login"
        noindex
      />
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        {/* Floating elements */}
        <motion.div 
          className="absolute top-1/4 right-[15%] w-64 h-64 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 left-[10%] w-72 h-72 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl"
          animate={{ 
            y: [0, -40, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        <div className="w-full max-w-md space-y-8 relative z-10">
          <div className="absolute top-0 left-0 -mt-16">
            <Button variant="ghost" size="sm" asChild className="group">
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
                Back to Home
              </Link>
            </Button>
          </div>

          <motion.div 
            className="text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mx-auto w-12 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 flex items-center justify-center text-white">
              <Code className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-bold">Welcome back</h1>
            <p className="text-zinc-600 dark:text-zinc-300">
              Enter your credentials to access your account
            </p>
          </motion.div>

          <motion.form 
            onSubmit={handleSubmit} 
            className="space-y-6 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Link
                  to="/auth/reset-password"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 relative group overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={loading}
            >
              <span className="relative z-10 flex items-center justify-center">
                {loading ? (
                  "Signing in..."
                ) : (
                  <>
                    <LogIn className="mr-2 h-5 w-5" />
                    Sign In
                  </>
                )}
              </span>
              <span className="spotlight" style={{ 
                '--x': `${mousePosition.x}px`,
                '--y': `${mousePosition.y}px`,
              } as React.CSSProperties}></span>
            </Button>
          </motion.form>

          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Don't have an account?{" "}
              <Link
                to="/auth/register"
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline"
              >
                Sign up
              </Link>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right side - Image/Illustration */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 relative overflow-hidden">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{ 
            backgroundImage: "linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px), linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}></div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <motion.div 
            className="max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative mb-8">
              {/* Glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-20 blur-xl"></div>
              
              <div className="relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 shadow-2xl">
                <img
                  src="/images/dashboard-preview.svg"
                  alt="LaunchGuide Dashboard"
                  className="w-full h-auto"
                  onError={(e) => {
                    e.currentTarget.src = "https://placehold.co/800x450/f8fafc/64748b?text=LaunchGuide+Dashboard";
                  }}
                />
              </div>
            </div>
            
            <motion.blockquote 
              className="bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg border border-zinc-200 dark:border-zinc-700 relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="absolute top-0 left-0 w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-br-[100px] -z-10"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-tl-[100px] -z-10"></div>
              
              <p className="text-lg font-medium text-zinc-700 dark:text-zinc-200 mb-4">
                "LaunchGuide has transformed how we market our products. It's like having an entire marketing team at your fingertips."
              </p>
              <footer className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-blue-200 dark:border-blue-800">
                  <img 
                    src="https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80" 
                    alt="User testimonial" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-semibold text-zinc-900 dark:text-white">Alex Chen</p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">CTO & Co-founder, DevMetrics</p>
                </div>
              </footer>
            </motion.blockquote>
          </motion.div>
        </div>
      </div>
    </div>
  );
}